import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, UserPlus, Shield, UserMinus, 
  Euro, Plus, Minus, Wallet, History
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const UserRow = ({ user, currentUserId, onRoleChange, onDelete }) => (
  <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
        <span className="text-sm font-medium text-white">
          {user.name?.charAt(0).toUpperCase() || 'U'}
        </span>
      </div>
      <div>
        <p className="font-medium text-white">{user.name}</p>
        <p className="text-sm text-zinc-500">{user.email}</p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <Select 
        value={user.role} 
        onValueChange={(v) => onRoleChange(user.id, v)}
        disabled={user.id === currentUserId}
      >
        <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="admin">Administrateur</SelectItem>
          <SelectItem value="collaborateur">Collaborateur</SelectItem>
        </SelectContent>
      </Select>
      <Button 
        size="icon" 
        variant="destructive" 
        className="h-9 w-9"
        onClick={() => onDelete(user.id)}
        disabled={user.id === currentUserId}
      >
        <UserMinus className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

const TransactionDialog = ({ open, onOpenChange, onSubmit, type }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(parseFloat(amount), description, type);
      setAmount('');
      setDescription('');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-white">
            {type === 'credit' ? 'Ajouter des fonds' : 'Retirer des fonds'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-zinc-300">Montant (€)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              min="0.01"
              step="0.01"
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="transaction-amount-input"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              className="bg-zinc-950 border-zinc-800 text-white"
              placeholder="Motif de la transaction"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className={type === 'credit' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}
              data-testid="transaction-submit-button"
            >
              {type === 'credit' ? 'Créditer' : 'Débiter'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdminPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditOpen, setCreditOpen] = useState(false);
  const [debitOpen, setDebitOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [usersRes, accountRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/account`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setAccount(accountRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success('Rôle mis à jour');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTransaction = async (amount, description, type) => {
    try {
      await axios.post(`${API}/account/transaction?montant=${amount}&description=${encodeURIComponent(description)}&type=${type}`, {}, { withCredentials: true });
      toast.success('Transaction effectuée');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la transaction');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 bg-zinc-800 rounded animate-pulse" />
        <div className="h-64 bg-zinc-800 rounded-lg animate-pulse" />
      </div>
    );
  }

  const recentTransactions = account?.historique?.slice(-5).reverse() || [];

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="admin-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Administration</h1>
        <p className="text-zinc-400 mt-1">Gérez les utilisateurs et le compte de la société</p>
      </div>

      {/* Company Account */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-500" />
              Compte de la société
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-6">
              <p className="text-zinc-500 text-sm uppercase tracking-wide mb-2">Solde actuel</p>
              <p className={`text-4xl font-bold font-mono ${(account?.solde || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {(account?.solde || 0).toLocaleString('fr-FR')} €
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                onClick={() => setCreditOpen(true)}
                data-testid="credit-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créditer
              </Button>
              <Button 
                className="flex-1 bg-red-500 hover:bg-red-600"
                onClick={() => setDebitOpen(true)}
                data-testid="debit-button"
              >
                <Minus className="w-4 h-4 mr-2" />
                Débiter
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-blue-500" />
              Dernières transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentTransactions.length > 0 ? (
              <div className="space-y-2">
                {recentTransactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded">
                    <div>
                      <p className="text-sm text-white">{tx.description}</p>
                      <p className="text-xs text-zinc-500">
                        {tx.date ? format(new Date(tx.date), 'dd/MM/yyyy HH:mm', { locale: fr }) : ''}
                      </p>
                    </div>
                    <span className={`font-mono text-sm ${tx.type === 'credit' ? 'text-emerald-500' : 'text-red-500'}`}>
                      {tx.type === 'credit' ? '+' : '-'}{tx.montant.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-zinc-500 py-4">Aucune transaction</p>
            )}
          </CardContent>
        </Card>
      </div>

      <TransactionDialog 
        open={creditOpen} 
        onOpenChange={setCreditOpen}
        onSubmit={handleTransaction}
        type="credit"
      />
      <TransactionDialog 
        open={debitOpen} 
        onOpenChange={setDebitOpen}
        onSubmit={handleTransaction}
        type="debit"
      />

      {/* Users Management */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-500" />
            Gestion des utilisateurs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {users.map((user) => (
            <UserRow 
              key={user.id}
              user={user}
              currentUserId={currentUser?.id}
              onRoleChange={handleRoleChange}
              onDelete={handleDeleteUser}
            />
          ))}
          {users.length === 0 && (
            <p className="text-center text-zinc-500 py-4">Aucun utilisateur</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
